import { Box3, type Object3D } from "three";
import { parseMeshName, floorLabel, floorOrder, type FloorKey } from "@safers/model";

/**
 * three 기반 층 인덱서 + ancestor 탐색.
 *
 * 순수 명명 파서(`parseMeshName` 등)는 `@safers/model`로 이전되어 app·admin 공용.
 * 여기에는 로드된 GLTF scene(Object3D)에 의존하는 함수만 남긴다.
 */

// 파서/타입은 @safers/model 재export — 기존 `@/lib/model-layers` import 경로 유지.
export { parseMeshName, type FloorKey } from "@safers/model";
export type { MeshKind, MeshLayerInfo } from "@safers/model";

export interface FloorRange {
  /** "F01" 등 */
  key: FloorKey;
  /** 표시용 라벨 (F01 → "1F", B1 → "B1", PH1 → "PH1") */
  label: string;
  /** floor의 y 시작 (SLAB 윗면 ≈ bbox.min.y) */
  yMin: number;
  /** floor의 y 끝 (다음 SLAB 윗면 또는 +∞) */
  yMax: number;
  /** 정렬용 숫자 키 (B1=-1, F01=1, PH1=91) */
  order: number;
}

/** 건물 1동의 층 목록 (건물마다 층 구성이 다르므로) */
export interface BuildingFloors {
  code: string;
  floors: FloorRange[];
}

export interface FloorIndex {
  /** 전 건물 union 층 목록 (낮→높) — altitude 매핑/요약용 */
  floors: FloorRange[];
  /** 모든 건물 ID */
  buildings: string[];
  /** 건물별 층 목록 (층별 단면 내비게이션용) */
  buildingFloors: BuildingFloors[];
  /** altitude(y) → FloorKey 매핑 */
  findFloorForY(y: number): FloorKey;
}

/**
 * Mesh 자체 이름이 자동생성(Mesh977 등)인 경우가 많으므로,
 * BIM 정보를 가진 ancestor 노드까지 탐색.
 */
export function findLayerInfo(obj: Object3D) {
  let cur: Object3D | null = obj;
  while (cur) {
    const info = parseMeshName(cur.name);
    if (info.buildingId || info.kind === "TMP" || info.kind === "GROUND") return info;
    cur = cur.parent;
  }
  return { buildingId: null, floor: null, kind: "OTHER" as const };
}

/**
 * 모델 root를 traverse해서 각 층의 y-range를 계산.
 * - 기본: SLAB mesh의 y-range 사용 (층 바닥 기준이 가장 정확)
 * - fallback: SLAB이 없는 층(예: 일부 모델의 F01)은 해당 층 mesh 전체의 bbox로 대체
 *   → SLAB 누락 층이 인덱스에서 빠지지 않게 (#63)
 */
type YRange = { yMin: number; yMax: number };

function accumulate(map: Map<string, YRange>, key: string, yMin: number, yMax: number) {
  const existing = map.get(key);
  if (existing) {
    existing.yMin = Math.min(existing.yMin, yMin);
    existing.yMax = Math.max(existing.yMax, yMax);
  } else {
    map.set(key, { yMin, yMax });
  }
}

/** 층 키 집합 → SLAB 우선(없으면 전체 bbox) y-range + 인접 층 chaining 된 FloorRange[] */
function buildRanges(
  keys: Iterable<FloorKey>,
  slabMap: Map<string, YRange>,
  anyMap: Map<string, YRange>,
  mapKey: (k: FloorKey) => string
): FloorRange[] {
  const sorted = [...keys]
    .map((key) => {
      const r = slabMap.get(mapKey(key)) ?? anyMap.get(mapKey(key))!;
      return { key, label: floorLabel(key), yMin: r.yMin, yMax: r.yMax, order: floorOrder(key) };
    })
    .sort((a, b) => a.order - b.order);
  // 인접 층 사이를 채워 floor 상한 = 다음 floor의 yMin (altitude 매핑 정확도)
  return sorted.map((f, i) => {
    const next = sorted[i + 1];
    return { ...f, yMax: next ? next.yMin : Number.POSITIVE_INFINITY };
  });
}

/**
 * 모델 root를 traverse해서 층 y-range를 계산 (union + 건물별).
 * SLAB 우선, 없으면 층 mesh 전체 bbox로 fallback(#63).
 */
export function buildFloorIndex(root: Object3D): FloorIndex {
  const slabByFloor = new Map<string, YRange>();
  const anyByFloor = new Map<string, YRange>();
  const slabByBF = new Map<string, YRange>(); // `${building} ${floor}`
  const anyByBF = new Map<string, YRange>();
  const buildings = new Set<string>();
  const buildingFloorKeys = new Map<string, Set<FloorKey>>();

  root.traverse((obj) => {
    const info = parseMeshName(obj.name);
    if (info.buildingId) {
      buildings.add(info.buildingId);
      if (!buildingFloorKeys.has(info.buildingId))
        buildingFloorKeys.set(info.buildingId, new Set());
    }
    if (!info.floor) return;

    const box = new Box3().setFromObject(obj);
    if (box.isEmpty()) return;

    accumulate(anyByFloor, info.floor, box.min.y, box.max.y);
    if (info.kind === "SLAB") accumulate(slabByFloor, info.floor, box.min.y, box.max.y);

    if (info.buildingId) {
      buildingFloorKeys.get(info.buildingId)!.add(info.floor);
      const bf = `${info.buildingId} ${info.floor}`;
      accumulate(anyByBF, bf, box.min.y, box.max.y);
      if (info.kind === "SLAB") accumulate(slabByBF, bf, box.min.y, box.max.y);
    }
  });

  const floors = buildRanges([...anyByFloor.keys()], slabByFloor, anyByFloor, (k) => k);

  const buildingFloors: BuildingFloors[] = [...buildingFloorKeys.entries()]
    .filter(([, keys]) => keys.size > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, keys]) => ({
      code,
      floors: buildRanges([...keys], slabByBF, anyByBF, (k) => `${code} ${k}`),
    }));

  function findFloorForY(y: number): FloorKey {
    if (floors.length === 0 || y < floors[0]!.yMin - 0.5) return "OUTSIDE";
    for (const f of floors) {
      if (y >= f.yMin && y < f.yMax) return f.key;
    }
    return floors[floors.length - 1]!.key;
  }

  return {
    floors,
    buildings: [...buildings].sort(),
    buildingFloors,
    findFloorForY,
  };
}
